import {
  BulkDeleteDialog,
  BulkSelectionToolbar,
  formatBulkCount,
  useBulkDelete,
} from '@/components/app/bulk-delete';
import { ConfirmDeleteDialog } from '@/components/app/confirm-delete-dialog';
import { ListPage, ListPagination } from '@/components/app/list-page';
import type { ListColumn } from '@/components/app/list-page';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ImportDataButton } from '@/features/imports';
import { useActiveOrganization } from '@/features/organization';
import {
  bulkDeleteBranchNote,
  isSharedAcrossBranches,
  useActiveLocation,
} from '@/features/organization-locations';
import {
  useDeleteService,
  useListServices,
  useRemoveServiceLocation,
  useUpdateService,
} from '@/features/organization-services';
import {
  NewServiceCategoryDialog,
  useListCategories,
} from '@/features/service-categories';
import { BookingPaymentWarningBanner } from '@/features/stripe-connect/components/booking-payment-warning-banner';
import { useOrgCurrency } from '@/hooks/use-org-currency';
import { micrositeBookingUrl } from '@/lib/microsite-url';
import type {
  ListedService,
  OrganizationService,
} from '@borradh-workspace/api-client/types';
import { servicePriceTypeLabels } from '@borradh-workspace/labels';
import { useNavigate } from '@tanstack/react-router';
import { Link2Icon, MoreHorizontal, Sparkles } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  AssignServicesToTeamButton,
  AssignServicesToTeamDialog,
} from './assign-services-to-team';
import { ImportServicesDialog } from './import-services-dialog';
import { useServicePagination } from './use-service-pagination';

export function ServicesPage() {
  const { data: activeOrg } = useActiveOrganization();
  const {
    location: activeLocation,
    locations,
    isMultiLocation,
  } = useActiveLocation();
  const { removeServiceLocationAsync, isRemoving } = useRemoveServiceLocation();
  const [importOpen, setImportOpen] = useState(false);
  // `isActive: undefined` = active AND archived. The hook hides archived rows
  // unless the key is present, which made this page a one-way door: "Archive"
  // removed a service from the only screen that lists services, so there was
  // nowhere left to un-archive it from. The CSV import made that visible —
  // "import as archived, so I can review them" imported services that could
  // never be reviewed. Archived rows carry an "Archived" badge and a Restore
  // action instead.
  const { services, isLoading, isError, error } = useListServices({
    isActive: undefined,
  });
  const { categories } = useListCategories();
  const { deleteService } = useDeleteService();
  // Quiet instance for bulk delete, which raises one summary toast instead.
  const { deleteServiceAsync: deleteServiceQuietly } = useDeleteService({
    showSuccessToast: false,
    showErrorToast: false,
  });
  // Separate update instance for archive so we can use a clearer toast.
  const { updateService: archiveService } = useUpdateService({
    showSuccessToast: false,
  });

  const navigate = useNavigate();
  const { format } = useOrgCurrency();
  const [search, setSearch] = useState('');

  const filteredServices = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return services;
    return services.filter((s) => s.name.toLowerCase().includes(term));
  }, [services, search]);

  const { rows, pagination } = useServicePagination(filteredServices, search);

  const rowIds = useMemo(() => rows.map((service) => service.id), [rows]);
  const bulk = useBulkDelete({
    deleteOne: deleteServiceQuietly,
    noun: { singular: 'service', plural: 'services' },
    rowIds,
    labelFor: (id) => services.find((s) => s.id === id)?.name,
    // A new search, page, page size or branch is a different set of rows.
    resetKey: `${search}|${pagination.pageIndex}|${pagination.pageSize}|${activeLocation?.id ?? ''}`,
  });
  const bulkBranchNote = bulkDeleteBranchNote({
    records: rows.filter((service) => bulk.selected.has(service.id)),
    totalBranches: locations.length,
  });
  // The other thing you can do with a selection: hand every selected service
  // to one or more team members at once, instead of opening each service and
  // ticking the same names.
  const [assignOpen, setAssignOpen] = useState(false);
  const selectedServices = useMemo(
    () =>
      rows
        .filter((service) => bulk.selected.has(service.id))
        .map((service) => ({ id: service.id, name: service.name })),
    [rows, bulk.selected]
  );

  // Build accent color per category id

  // Group services by categoryId (or UNCATEGORISED_KEY)

  // Order sections to match the sidebar (categories first, uncategorised last)

  // Create/edit are PAGES now (the unified editor), not dialogs. A category's
  // "Add" button carries its category through as a search param so the new
  // service lands in the right section.
  const openAdd = (_initialCategoryId: string | null = null) =>
    navigate({ to: '/create/$entity', params: { entity: 'service' } });
  const openEdit = (service: ListedService) =>
    navigate({
      to: '/edit/$entity/$id',
      params: { entity: 'service', id: service.id },
    });

  const handleArchive = (service: ListedService) => {
    const restoring = !service.isActive;
    archiveService(
      { id: service.id, isActive: restoring },
      {
        onSuccess: () =>
          toast.success(restoring ? 'Service restored' : 'Service archived'),
      }
    );
  };

  // Was a raw `window.confirm` — an unstyled, unbranded browser dialog that no
  // test could drive. Now the shared destructive confirmation.
  // `ListedService`, not `OrganizationService`: the delete prompt needs the
  // row's `locationIds` to know whether it is deleting from one branch or all.
  const [deleteTarget, setDeleteTarget] = useState<ListedService | null>(null);
  const handleDelete = (service: ListedService) => setDeleteTarget(service);

  const handleCopyBookingLink = () => {
    const slug = activeOrg?.slug;
    if (!slug) return;
    // The CANONICAL customer url, not one that survives on a redirect: this
    // gets pasted into an Instagram bio and lives there for years.
    const url = micrositeBookingUrl(slug);
    navigator.clipboard
      .writeText(url)
      .then(() => {
        toast.success('Booking link copied to clipboard');
      })
      .catch(() => {
        toast.error('Failed to copy booking link');
      });
  };

  const categoryName = (service: OrganizationService) =>
    categories.find((category) => category.id === service.categoryId)?.name ??
    'Uncategorised';

  // Which branches this service is on decides whether deleting it is a local
  // act or a business-wide one — see `isSharedAcrossBranches` for why an EMPTY
  // link set counts as shared.
  const sharedAcrossBranches = isSharedAcrossBranches({
    locationIds: deleteTarget?.locationIds,
    totalBranches: locations.length,
  });
  const branchLabel =
    activeLocation?.name ?? activeLocation?.addressLine1 ?? 'this location';

  const columns: ListColumn<ListedService>[] = [
    {
      id: 'name',
      header: 'Name',
      mobile: 'primary',
      cell: (service) => <span className="font-medium">{service.name}</span>,
    },
    {
      id: 'category',
      header: 'Category',
      // Categories were a 280px sidebar filter and a set of grouped sections.
      // As a column they are visible on every row, sortable with the rest, and
      // they stop costing a quarter of the page width.
      mobile: 'secondary',
      cell: categoryName,
    },
    {
      id: 'duration',
      header: 'Duration',
      cell: (service) =>
        service.appointmentDuration
          ? `${service.appointmentDuration} min`
          : '—',
    },
    {
      id: 'price',
      header: 'Price',
      align: 'right',
      mobile: 'trailing',
      cell: (service) =>
        service.priceCents == null
          ? servicePriceTypeLabels[service.priceType]
          : format(service.priceCents),
    },
    {
      id: 'status',
      header: 'Status',
      cell: (service) => (
        <Badge variant={service.isActive ? 'default' : 'secondary'}>
          {service.isActive ? 'Active' : 'Archived'}
        </Badge>
      ),
    },
  ];

  return (
    <>
      <title>Services | Borrah</title>

      <ListPage<ListedService>
        config={{
          title: 'Services',
          // Services customers can't book online because they require
          // payment Stripe can't take.
          banner: <BookingPaymentWarningBanner />,
          columns,
          rows,
          footer:
            !isLoading && !isError ? (
              <ListPagination {...pagination} />
            ) : undefined,
          rowKey: (service) => service.id,
          onRowClick: openEdit,
          rowActions: (service) => (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  aria-label={`Actions for ${service.name}`}
                  size="icon"
                  variant="ghost"
                >
                  <MoreHorizontal className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => openEdit(service)}>
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleArchive(service)}>
                  {service.isActive ? 'Archive' : 'Restore'}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => handleDelete(service)}
                  variant="destructive"
                >
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ),
          searchPlaceholder: 'Search services',
          search,
          onSearchChange: setSearch,
          primaryAction: {
            label: 'Add Service',
            mobileLabel: 'Add',
            onClick: () => openAdd(),
            // The caret only exists where there is another location to copy from.
            menu: isMultiLocation
              ? {
                  items: [
                    { label: 'New service', onSelect: () => openAdd() },
                    {
                      label: 'Import from another location…',
                      onSelect: () => setImportOpen(true),
                    },
                  ],
                }
              : undefined,
          },
          secondaryAction: <NewServiceCategoryDialog />,
          selection: bulk.selection,
          toolbar: bulk.hasSelection ? (
            <BulkSelectionToolbar
              actions={
                <AssignServicesToTeamButton
                  disabled={bulk.isDeleting}
                  onClick={() => setAssignOpen(true)}
                />
              }
              bulk={bulk}
            />
          ) : (
            <>
              <ImportDataButton kind="services" />
              <Button
                className="gap-1.5"
                disabled={!activeOrg?.slug}
                onClick={handleCopyBookingLink}
                size="sm"
                variant="outline"
              >
                <Link2Icon className="size-4" />
                <span className="hidden sm:inline">Copy Booking Link</span>
              </Button>
            </>
          ),
          isLoading,
          isError,
          errorMessage: error?.message ?? 'Failed to load services',
          empty: {
            icon: Sparkles,
            title: search ? 'No matching services' : 'No services yet',
            description: search
              ? 'No service matches that search.'
              : 'Add your first service to start taking bookings.',
          },
        }}
      />
      <ImportServicesDialog onOpenChange={setImportOpen} open={importOpen} />

      <AssignServicesToTeamDialog
        onAssigned={bulk.clear}
        onOpenChange={setAssignOpen}
        open={assignOpen}
        services={selectedServices}
      />

      <ConfirmDeleteDialog
        // The consequence differs by SCOPE, so the description has to as well:
        // on a shared service the destructive button reaches every branch, and
        // saying "from your catalog" would understate it.
        description={
          sharedAcrossBranches
            ? `“${deleteTarget?.name}” is offered at other locations too. Removing it here leaves it in place for them; deleting removes it from every location's booking page. Appointments already booked are not affected either way.`
            : 'This permanently removes the service from your catalog and from your public booking page. Appointments already booked against it are not affected.'
        }
        confirmLabel={sharedAcrossBranches ? 'Delete everywhere' : 'Delete'}
        isPending={isRemoving}
        onConfirm={() => {
          if (deleteTarget) deleteService(deleteTarget.id);
          setDeleteTarget(null);
        }}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        open={!!deleteTarget}
        secondaryAction={
          sharedAcrossBranches && activeLocation
            ? {
                label: `Remove from ${branchLabel}`,
                onClick: async () => {
                  if (!deleteTarget) return;
                  const target = deleteTarget;
                  setDeleteTarget(null);
                  await removeServiceLocationAsync({
                    serviceId: target.id,
                    locationId: activeLocation.id,
                  }).catch(() => {
                    // The hook already surfaced the server's message — including
                    // the "last location" refusal, which is the one an operator
                    // needs to read.
                  });
                },
              }
            : undefined
        }
        title={`Delete "${deleteTarget?.name}"?`}
      />

      <BulkDeleteDialog
        bulk={bulk}
        description={
          <>
            This permanently removes {formatBulkCount(bulk.count, bulk.noun)}{' '}
            from your catalog and from your public booking page. Appointments
            already booked are not affected.
            {bulkBranchNote && ` ${bulkBranchNote}`}
          </>
        }
      />
    </>
  );
}
